/**
 * DetailCard — full-screen node detail surface for the Environment graph.
 *
 * Cycle-105.8 sprint-3 (S3, g231) · FR-E-4 (tasks 231.1/231.2/231.3).
 * Replaces the 280px `FloatingInspector` with a full-screen card opened on
 * node selection. Five tabs — Overview / Map & Lineage / Assets / Evidence /
 * Certificate — over a two-column layout with the lane-based `LineageMiniMap`
 * pinned to the right sidebar.
 *
 * Structurally faithful to the canonical handoff
 * (`design_handoff_echelon_2026_04_26/environment/detail.jsx`) but driven by
 * REAL graph data (`GraphNode` + the D3 graph), with honest empty states —
 * no mock content, no dead controls (project rule: no fake data / no dead
 * buttons). NFR-3: the Certificate tab reuses `Inspector.tsx`
 * (`TheatreModuleInspector`) rather than re-implementing certificate rendering.
 *
 * Cycle-115 FR-B1 (sprint-233): PROMOTE-TO-THEATRE-TEMPLATE is wired to the real
 * `POST /api/v1/knowledge/nodes/{id}/promote-to-template` route. It is enabled
 * only for VALIDATED knowledge nodes (the route 409s otherwise); every
 * PROVISIONAL node — including all Chorus-coupled briefs — shows an honest
 * disabled state with an explanatory tooltip (R-9), never surfacing a 409.
 *
 * Cycle-fe-1 sprint-238 (S3, g238) · FR-S3: Overview gains CONTENT + HYPOTHESIS
 * sections (backend `node.metadata` truth only, honest empty — NO generated
 * fallback, NFR-2/NFR-3), and the right sidebar becomes a LINEAGE | ASSETS tab
 * pair: LINEAGE mounts the radial `LineageSunburst` (depth control 1|2|3,
 * default 2) through an error boundary that falls back to `LineageMiniMap`;
 * ASSETS reuses the existing `AssetsTab`.
 *
 * Cycle-fe-1 sprint-241 (S6) · Verification-Loop Slice 1: knowledge nodes
 * become loop objects on the Overview — freshness micro-label (seeded clamp
 * via `nodeFreshness`), "Compiled from N" certificate-lineage line (counted
 * from the already-fetched COMPILED_FROM edges, zero new fetches), a
 * domain-scoped "Live signals →" deep-link back to the Workspace globe, and
 * the SEEDED · BASELINE status treatment (DB truth at lower visual weight —
 * never hidden). The design checkpoint (241.5) found NO freshness/seeded
 * node treatment in the ambient frames (the dashed amber ring there is the
 * authority-gate concept), so this is the minimal honest interim using only
 * the existing idiom — canvas treatment deferred to the visual remainder.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/api/client';
import type { GraphNode } from './graph/useGraphData';
import type { D3GraphNode, D3GraphEdge } from './graph/D3GraphCanvas';
import { NODE_CLASSES } from './graph/D3GraphCanvas';
import { isSeededNode, nodeFreshness } from './graph/freshness';
import { countCompiledFrom } from './graph/provenance';
import { LineageSunburst, LineageSunburstBoundary } from './LineageSunburst';
import { TheatreModuleInspector } from './Inspector';
import { InvestigationWork } from './InvestigationWork';
import type { TheatreData } from './types';

type TabId = 'overview' | 'lineage' | 'assets' | 'evidence' | 'cert';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'lineage', label: 'Map & Lineage' },
  { id: 'assets', label: 'Assets' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'cert', label: 'Certificate' },
];

const STATUS_DOT: Record<string, string> = {
  idle: 'oklch(0.380 0.007 265)',
  running: 'oklch(0.530 0.205 295)',
  active: 'oklch(0.530 0.205 295)',
  completed: 'oklch(0.568 0.160 152)',
  verified: 'oklch(0.688 0.165 72)',
  certified: 'oklch(0.688 0.165 72)',
  failed: 'oklch(0.615 0.208 22)',
  stale: 'oklch(0.480 0.007 265)',
};

function d3TypeColour(d3Type: string | undefined): string {
  return NODE_CLASSES[d3Type ?? '']?.color ?? 'oklch(0.480 0.007 265)';
}

/**
 * Globe deep-link for a knowledge node's domain (sprint-241 FR-S6.3):
 * `globe=1` opens the Workspace globe takeover on arrival; `domain` is a
 * best-effort layer-scope hint consumed once by InvestigationWorkspacePageV2.
 * Exported for vitest.
 */
export function liveSignalsUrl(domain: string): string {
  return `/workspace?globe=1&domain=${encodeURIComponent(domain)}`;
}

/** Extract a human-readable detail from an axios error (FR-B1 promote feedback). */
function promoteErrorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  return detail || 'Promotion failed. Please try again.';
}

export interface DetailCardProps {
  node: GraphNode | null;
  nodeId: string | null;
  /** D3 graph nodes + edges — drive the lineage MiniMap and chain. */
  nodes: D3GraphNode[];
  edges: D3GraphEdge[];
  /** Theatre scoping the Environment (for the reused Certificate panel). */
  theatreId: string | null;
  onClose: () => void;
  onNavigate?: (path: string) => void;
  onDelete?: (nodeId: string) => void;
  /** Re-point selection (clicking a node in the MiniMap). */
  onSelectNode?: (id: string) => void;
}

export function DetailCard({
  node,
  nodeId,
  nodes,
  edges,
  theatreId,
  onClose,
  onNavigate,
  onDelete,
  onSelectNode,
}: DetailCardProps) {
  const [tab, setTab] = useState<TabId>('overview');
  // FR-S3 (sprint-238): sidebar-local LINEAGE | ASSETS tab + sunburst depth.
  const [sideTab, setSideTab] = useState<'lineage' | 'assets'>('lineage');
  const [sunburstDepth, setSunburstDepth] = useState<1 | 2 | 3>(2);
  const queryClient = useQueryClient();

  // FR-B1 (cycle-115): PROMOTE wires to the real promote-to-template route.
  // Only VALIDATED nodes are promotable (the route 409s otherwise,
  // knowledge_routes.py:265-271); PROVISIONAL nodes (incl. all Chorus briefs)
  // are disabled with an honest tooltip — see OverviewBody.
  const promoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(
        `/api/v1/knowledge/nodes/${id}/promote-to-template`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-graph-d3'] });
    },
  });

  // Reset to Overview + clear promote feedback whenever the node changes.
  useEffect(() => {
    setTab('overview');
    setSideTab('lineage');
    promoteMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  // ESC closes the card.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The matching D3 node carries the canonical type/colour for the accent.
  const d3Node = useMemo(() => nodes.find((n) => n.id === nodeId) ?? null, [nodes, nodeId]);

  // Sprint-241 FR-S6.2: certificate-lineage count from the edges already in
  // memory (COMPILED_FROM targets this node) — zero new fetches.
  const compiledFromCount = useMemo(
    () => (nodeId ? countCompiledFrom(nodeId, edges) : 0),
    [nodeId, edges],
  );

  if (!node || !nodeId) return null;

  const accent = d3TypeColour(d3Node?.type);
  const status = d3Node?.status ?? (node.metadata?.status as string) ?? 'idle';
  const statusColor = STATUS_DOT[status] ?? 'oklch(0.480 0.007 265)';
  const investigationId = node.evidence_bundle_ref?.investigation_id;

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col animate-in fade-in duration-150"
      style={{ background: 'var(--e-env-bg-app)' }}
      data-testid="environment-detail-card"
      role="dialog"
      aria-label={`Detail · ${node.title}`}
    >
      {/* ── Top bar ── */}
      <div
        className="flex items-center gap-3.5 px-6 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--e-env-border-subtle)' }}
      >
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 font-mono text-[12px] text-[var(--e-text-secondary)] hover:text-[var(--e-text-primary)] rounded px-2.5 py-1.5"
          style={{ border: '1px solid var(--e-env-border)' }}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>

        <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[var(--e-text-muted)]">
          {node.node_type}
          <span style={{ color: accent, marginLeft: 8 }}>· {status}</span>
        </div>

        <div className="flex-1 min-w-0 truncate text-[18px] font-medium text-[var(--e-text-primary)] tracking-[-0.01em]">
          {node.title}
          {node.subtitle && (
            <span className="font-mono text-[11px] text-[var(--e-text-muted)] ml-2.5">{node.subtitle}</span>
          )}
        </div>

        <button
          onClick={onClose}
          className="font-mono text-[11px] text-[var(--e-text-muted)] hover:text-[var(--e-text-secondary)] rounded px-2 py-1"
          style={{ border: '1px solid var(--e-env-border)' }}
        >
          ESC
        </button>
      </div>

      {/* ── Tabs ── */}
      <div
        className="flex gap-0.5 px-6 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--e-env-border-subtle)' }}
        role="tablist"
        aria-label="Detail tabs"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'font-mono text-[10.5px] px-3.5 py-2.5 uppercase tracking-[0.06em] -mb-px',
              tab === t.id ? 'text-[var(--e-text-primary)]' : 'text-[var(--e-text-muted)] hover:text-[var(--e-text-secondary)]',
            )}
            style={{ borderBottom: tab === t.id ? `1px solid ${accent}` : '1px solid transparent' }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Body: content + sidebar ── */}
      <div className="flex-1 min-h-0 grid" style={{ gridTemplateColumns: '1fr 360px' }}>
        {/* LEFT — active tab */}
        <div className="overflow-y-auto px-[max(40px,6vw)] py-7">
          <div className="mx-auto" style={{ maxWidth: 720 }}>
            {tab === 'overview' && (
              <OverviewBody
                node={node}
                statusColor={statusColor}
                status={status}
                compiledFromCount={compiledFromCount}
                investigationId={investigationId}
                canPromote={node.verification_state === 'VALIDATED'}
                onPromote={() => promoteMutation.mutate(node.node_id)}
                promoteState={{
                  pending: promoteMutation.isPending,
                  success: promoteMutation.isSuccess,
                  error: promoteMutation.isError
                    ? promoteErrorMessage(promoteMutation.error)
                    : null,
                }}
                onNavigate={onNavigate}
                onDelete={onDelete}
              />
            )}
            {tab === 'lineage' && <LineageChain nodeId={nodeId} nodes={nodes} edges={edges} onSelect={onSelectNode} />}
            {tab === 'assets' && <AssetsTab nodeId={nodeId} nodeType={node.node_type} />}
            {tab === 'evidence' && <EvidenceBody node={node} onNavigate={onNavigate} />}
            {tab === 'cert' && <CertificateTab theatreId={theatreId} title={node.title} />}
          </div>
        </div>

        {/* RIGHT — lineage sidebar */}
        <div
          className="flex flex-col min-h-0"
          style={{
            borderLeft: `2px solid ${accent}`,
            background: `linear-gradient(to right, color-mix(in oklch, ${accent} 5%, transparent) 0%, transparent 18%)`,
          }}
        >
          <div
            className="flex items-center justify-between px-4 pt-2.5 flex-shrink-0"
            style={{ borderBottom: '1px solid var(--e-env-border-subtle)' }}
          >
            <div className="flex gap-0.5" role="tablist" aria-label="Lineage sidebar tabs">
              {([['lineage', 'LINEAGE'], ['assets', 'ASSETS']] as const).map(([id, label]) => (
                <button
                  key={id}
                  role="tab"
                  aria-selected={sideTab === id}
                  data-testid={`sidebar-tab-${id}`}
                  onClick={() => setSideTab(id)}
                  className={cn(
                    'font-mono text-[10px] uppercase tracking-[0.14em] px-2.5 pb-2 -mb-px',
                    sideTab === id ? 'text-[var(--e-text-primary)]' : 'text-[var(--e-text-muted)] hover:text-[var(--e-text-secondary)]',
                  )}
                  style={{ borderBottom: sideTab === id ? `1px solid ${accent}` : '1px solid transparent' }}
                >
                  {label}
                </button>
              ))}
            </div>
            {sideTab === 'lineage' && (
              <div className="flex items-center gap-1 pb-2" role="group" aria-label="Sunburst depth">
                {([1, 2, 3] as const).map((d) => (
                  <button
                    key={d}
                    aria-pressed={sunburstDepth === d}
                    data-testid={`sunburst-depth-${d}`}
                    onClick={() => setSunburstDepth(d)}
                    className="font-mono text-[10px] px-2 py-0.5 rounded"
                    style={{
                      border: '1px solid var(--e-env-border)',
                      color: sunburstDepth === d ? accent : 'var(--e-text-muted)',
                      background: sunburstDepth === d ? `color-mix(in oklch, ${accent} 12%, transparent)` : 'transparent',
                    }}
                  >
                    {d}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex-1 min-h-0 p-2.5 flex flex-col overflow-y-auto">
            {sideTab === 'lineage' ? (
              <LineageSunburstBoundary nodeId={nodeId} node={d3Node} nodes={nodes} edges={edges} onSelect={onSelectNode}>
                <LineageSunburst nodeId={nodeId} node={d3Node} nodes={nodes} edges={edges} onSelect={onSelectNode} depth={sunburstDepth} />
              </LineageSunburstBoundary>
            ) : (
              <AssetsTab nodeId={nodeId} nodeType={node.node_type} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Overview ──────────────────────────────────────────────────────────────

function OverviewBody({
  node,
  statusColor,
  status,
  compiledFromCount,
  investigationId,
  canPromote,
  onPromote,
  promoteState,
  onNavigate,
  onDelete,
}: {
  node: GraphNode;
  statusColor: string;
  status: string;
  compiledFromCount: number;
  investigationId?: string;
  canPromote: boolean;
  onPromote: () => void;
  promoteState: { pending: boolean; success: boolean; error: string | null };
  onNavigate?: (path: string) => void;
  onDelete?: (nodeId: string) => void;
}) {
  const tags = useMemo(() => buildTags(node), [node]);
  const metrics = useMemo(() => buildMetrics(node), [node]);
  // FR-S3 (sprint-238): backend metadata truth only — NO generated fallback
  // (NFR-2/NFR-3; the handoff's `hypothesisFor(node)` is deliberately not ported).
  const content = metadataString(node, 'content');
  const hypothesis = metadataString(node, 'hypothesis');

  // Sprint-241 loop-object derivations (knowledge nodes only). Seeded =
  // attestation_id sentinel (rule a/d single source of truth); freshness is
  // already seeded-clamped by `nodeFreshness` (rule b).
  const isKnowledge = node.node_type === 'knowledge';
  const seeded = isKnowledge && isSeededNode(node);
  const freshness = isKnowledge ? nodeFreshness(node) : null;
  const domain = metadataString(node, 'domain');

  return (
    // Rule (a): seeded nodes render from DB truth at LOWER visual weight —
    // opacity treatment only (existing idiom; never hidden, no invented
    // badges/halos). Graduated nodes (sentinel replaced) render full-weight.
    <div className="flex flex-col gap-7" data-seeded={seeded || undefined} style={seeded ? { opacity: 0.78 } : undefined}>
      {/* Type + status (read-only — not a dead toggle) */}
      <Section label="Type">
        <div className="flex items-center gap-2">
          <span
            className="font-mono text-[11px] px-3 py-1 rounded-full"
            style={{ border: '1px solid var(--e-env-border)', color: 'var(--e-text-secondary)' }}
          >
            {node.node_type}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
            <span className="font-mono text-[10px] text-[var(--e-text-muted)]">{status}</span>
          </span>
          {/* Freshness micro-label (FR-S6.1) — honest 'unknown' when the
              backend has no parseable updated_at; placeholder thresholds
              documented in graph/freshness.ts (design checkpoint pending). */}
          {freshness && (
            <span
              className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--e-text-muted)]"
              data-testid="node-freshness"
            >
              freshness · {freshness}
            </span>
          )}
          {/* SEEDED · BASELINE status label (FR-S6.4 rule a) — DB truth,
              honest epistemic status in the existing font-mono register. */}
          {seeded && (
            <span
              className="font-mono text-[10px] uppercase tracking-[0.08em] px-2 py-[2px] rounded-full opacity-70"
              style={{ border: '1px solid var(--e-env-border)', color: 'var(--e-text-muted)' }}
              data-testid="seeded-baseline-label"
            >
              SEEDED · BASELINE
            </span>
          )}
        </div>
      </Section>

      {/* Graph tags — derived from real fields */}
      {tags.length > 0 && (
        <Section label="Graph Tags">
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t, i) => (
              <span
                key={i}
                className="font-mono text-[11px] px-2.5 py-[3px] rounded-full text-[var(--e-text-secondary)]"
                style={{ background: 'var(--e-env-bg-card)', border: '1px solid var(--e-env-border)' }}
              >
                {t}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Content — real node metadata, honest empty otherwise (FR-S3) */}
      <Section label="Content">
        <p className="text-[13.5px] leading-[1.65] m-0" style={{ color: content ? 'var(--e-text-primary)' : 'var(--e-text-muted)' }}>
          {content || 'No content yet.'}
        </p>
      </Section>

      {/* Compiled-from provenance (FR-S6.2) — counted from the COMPILED_FROM
          edges already in the fetched payload; honest zero when the node has
          no certificate lineage (e.g. seeded baselines, Chorus briefs). */}
      {isKnowledge && (
        <Section label="Compiled From">
          <p
            className="font-mono text-[11px] m-0"
            style={{ color: compiledFromCount > 0 ? 'var(--e-text-secondary)' : 'var(--e-text-muted)' }}
            data-testid="compiled-from-count"
          >
            {compiledFromCount > 0
              ? `Compiled from ${compiledFromCount} attestation${compiledFromCount === 1 ? '' : 's'}`
              : 'Compiled from 0 attestations — no certificate lineage yet.'}
          </p>
        </Section>
      )}

      {/* Summary — real verification summary, honest empty otherwise */}
      <Section label="Summary">
        <p className="text-[13.5px] leading-[1.65] m-0" style={{ color: node.verification_summary ? 'var(--e-text-primary)' : 'var(--e-text-muted)' }}>
          {node.verification_summary || 'No summary yet — this node has not produced a compiled brief.'}
        </p>
      </Section>

      {/* Hypothesis — real node metadata, honest empty otherwise (FR-S3) */}
      <Section label="Hypothesis">
        <p className="text-[13.5px] leading-[1.65] m-0" style={{ color: hypothesis ? 'var(--e-text-primary)' : 'var(--e-text-muted)' }}>
          {hypothesis || 'No hypothesis recorded.'}
        </p>
      </Section>

      {/* Work (#672): the investigation's work requirements and on-demand checks.
          Results never change evidence counts, readiness or corroboration_score. */}
      {node.node_type === 'investigation' && investigationId && <InvestigationWork investigationId={investigationId} />}

      {/* Metrics — only real values */}
      {metrics.length > 0 && (
        <Section label="Metrics">
          <div className="flex flex-wrap gap-4">
            {metrics.map((m) => (
              <MetricPill key={m.label} label={m.label} value={m.value} color={m.color} />
            ))}
          </div>
        </Section>
      )}

      {/* Live-signals deep-link (FR-S6.3) — domain-scoped link back to the
          Workspace globe; the domain is a best-effort layer-scope hint
          consumed once on arrival (InvestigationWorkspacePageV2). Knowledge
          nodes without a metadata.domain get no link (honest absence). */}
      {isKnowledge && domain && (
        <button
          onClick={() => onNavigate?.(liveSignalsUrl(domain))}
          className="flex items-center gap-1.5 font-mono text-[11px] text-[#14b8a6] hover:underline w-fit"
          data-testid="live-signals-link"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Live signals →
        </button>
      )}

      {/* Deep-link for investigations (preserved from FloatingInspector) */}
      {node.node_type === 'investigation' && investigationId && (
        <button
          onClick={() => onNavigate?.(`/workspace/investigations/${investigationId}`)}
          className="flex items-center gap-1.5 font-mono text-[11px] text-[#14b8a6] hover:underline w-fit"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Open in Workspace
        </button>
      )}

      {/* PROMOTE — FR-B1 (cycle-115): wired to promote-to-template; enabled only
          for VALIDATED nodes. PROVISIONAL (incl. all Chorus briefs) → honest
          disabled state with an explanatory tooltip (R-9). */}
      <div className="flex flex-col gap-2">
        <button
          onClick={onPromote}
          disabled={!canPromote || promoteState.pending}
          title={
            canPromote
              ? undefined
              : 'Only VALIDATED knowledge nodes can be promoted to a theatre template. PROVISIONAL nodes (including Chorus briefs) are validated through the certificate lifecycle first.'
          }
          className="font-mono text-[11px] tracking-[0.08em] px-3.5 py-2.5 rounded w-fit disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: 'color-mix(in oklch, oklch(0.510 0.168 278) 16%, transparent)',
            border: '1px solid oklch(0.510 0.168 278)',
            color: 'oklch(0.720 0.150 278)',
          }}
          data-testid="promote-to-theatre-template"
        >
          {promoteState.pending ? 'PROMOTING…' : 'PROMOTE TO THEATRE TEMPLATE →'}
        </button>
        {!canPromote && (
          <p className="font-mono text-[10.5px] text-[var(--e-text-muted)] leading-relaxed" role="note">
            {node.verification_state === 'PROVISIONAL'
              ? 'PROVISIONAL — Chorus briefs are validated through the certificate lifecycle before they can be promoted.'
              : 'Only VALIDATED knowledge nodes can be promoted to a theatre template.'}
          </p>
        )}
        {promoteState.success && (
          <p className="font-mono text-[10.5px] leading-relaxed" style={{ color: 'oklch(0.720 0.140 152)' }} role="status">
            Promoted to theatre template.
          </p>
        )}
        {promoteState.error && (
          <p className="font-mono text-[10.5px] leading-relaxed" style={{ color: 'oklch(0.700 0.170 22)' }} role="alert">
            {promoteState.error}
          </p>
        )}
      </div>

      {/* Remove from composition (preserved from FloatingInspector) */}
      {onDelete && (
        <button
          onClick={() => onDelete(node.node_id)}
          className="flex items-center gap-1.5 font-mono text-[10px] text-[#ef4444]/70 hover:text-[#ef4444] transition-colors w-fit pt-2"
          style={{ borderTop: '1px solid var(--e-env-border-subtle)' }}
        >
          <XCircle className="w-3 h-3" /> Remove from composition
        </button>
      )}
    </div>
  );
}

// ── Lineage chain (left, Map & Lineage tab) ─────────────────────────────────

function LineageChain({
  nodeId,
  nodes,
  edges,
  onSelect,
}: {
  nodeId: string;
  nodes: D3GraphNode[];
  edges: D3GraphEdge[];
  onSelect?: (id: string) => void;
}) {
  const chain = useMemo(() => {
    const edgeId = (e: string | D3GraphNode) => (typeof e === 'string' ? e : e.id);
    const parentsOf: Record<string, string[]> = {};
    const childrenOf: Record<string, string[]> = {};
    edges.forEach((e) => {
      const from = edgeId(e.source);
      const to = edgeId(e.target);
      (childrenOf[from] = childrenOf[from] || []).push(to);
      (parentsOf[to] = parentsOf[to] || []).push(from);
    });
    // Walk the primary trunk: ancestors (oldest first) → selected → descendants.
    const up: string[] = [];
    let cur = nodeId;
    for (let i = 0; i < 12; i++) {
      const p = (parentsOf[cur] || [])[0];
      if (!p || up.includes(p) || p === nodeId) break;
      up.unshift(p);
      cur = p;
    }
    const down: string[] = [];
    cur = nodeId;
    for (let i = 0; i < 6; i++) {
      const c = (childrenOf[cur] || [])[0];
      if (!c || down.includes(c) || c === nodeId) break;
      down.push(c);
      cur = c;
    }
    return [...up, nodeId, ...down];
  }, [nodeId, edges]);

  if (chain.length <= 1) {
    return <EmptyTab title="Chain · oldest → newest">No lineage recorded for this node yet.</EmptyTab>;
  }

  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--e-text-secondary)] mb-3.5">
        Chain · oldest → newest
      </div>
      {chain.map((id, i) => {
        const n = nodes.find((x) => x.id === id);
        if (!n) return null;
        const color = d3TypeColour(n.type);
        const isSelected = id === nodeId;
        return (
          <div key={id} className="flex gap-3 items-start pb-3.5">
            <div className="flex flex-col items-center">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: color, outline: isSelected ? `2px solid ${color}55` : 'none', outlineOffset: 2 }} />
              {i < chain.length - 1 && <div className="flex-1 w-px min-h-[28px]" style={{ background: 'var(--e-env-border-subtle)' }} />}
            </div>
            <button onClick={() => onSelect?.(id)} className="text-left" disabled={isSelected}>
              <div className={cn('text-[13px]', isSelected ? 'text-[var(--e-text-primary)] font-medium' : 'text-[var(--e-text-secondary)] hover:text-[var(--e-text-primary)]')}>
                {n.label}
              </div>
              <div className="font-mono text-[10.5px] text-[var(--e-text-muted)]">
                {n.type}
                {n.status && n.status !== 'idle' ? ` · ${n.status}` : ''}
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── Assets (FR-B3: node-scoped run artifacts) ───────────────────────────────

interface NodeArtifact {
  artifact_id: string;
  name: string;
  mime_type: string;
  ref: string;
  created_at: string | null;
}

interface NodeArtifactsResponse {
  node_id: string;
  artifacts: NodeArtifact[];
  count: number;
  next_cursor: string | null;
}

/** Node types that resolve to a run and can therefore carry artifacts. */
const ARTIFACT_NODE_TYPES = ['knowledge', 'attestation', 'run'];

/**
 * Assets tab — replaces the former hardcoded EmptyTab (FR-B3, cycle-115
 * sprint-234). Queries the real node-scoped artifacts endpoint and renders the
 * run's module outputs + generated candidates, with an honest empty state when
 * the node has no run payload (e.g. synthesized Chorus briefs) or is a node type
 * with no run lineage. A 404 (inaccessible/sentinel node) degrades to the same
 * honest empty — never a broken error surface.
 */
function AssetsTab({ nodeId, nodeType }: { nodeId: string; nodeType: string }) {
  const canHaveArtifacts = ARTIFACT_NODE_TYPES.includes(nodeType);
  const { data, isLoading, isError } = useQuery<NodeArtifactsResponse>({
    queryKey: ['environment-node-artifacts', nodeId],
    queryFn: async () => {
      const res = await apiClient.get<NodeArtifactsResponse>(
        `/api/v1/environment/nodes/${encodeURIComponent(nodeId)}/artifacts`,
        { params: { limit: 50 } },
      );
      return res.data;
    },
    enabled: canHaveArtifacts,
  });

  if (!canHaveArtifacts) {
    return (
      <EmptyTab title="Assets">
        This node type has no run-scoped artifacts. Run a theatre composition to produce module outputs and
        generated candidates.
      </EmptyTab>
    );
  }
  if (isLoading) {
    return <EmptyTab title="Assets">Loading run artifacts…</EmptyTab>;
  }
  if (isError || !data || data.count === 0) {
    return (
      <EmptyTab title="Assets">
        No run artifacts for this node yet. Assets appear after a theatre run; synthesized briefs (Chorus) have
        no run payload.
      </EmptyTab>
    );
  }
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--e-text-secondary)] mb-3.5">
        Run Artifacts · {data.count}
      </div>
      <div className="flex flex-col gap-2">
        {data.artifacts.map((a) => (
          <div
            key={a.artifact_id}
            className="flex items-center justify-between px-3.5 py-2.5 rounded-md"
            style={{ border: '1px solid var(--e-env-border-subtle)', background: 'var(--e-env-bg-panel)' }}
          >
            <div className="min-w-0">
              <div className="text-[13px] text-[var(--e-text-primary)] truncate">{a.name}</div>
              <div className="font-mono text-[10.5px] text-[var(--e-text-muted)] truncate">{a.ref}</div>
            </div>
            {a.created_at && (
              <div className="font-mono text-[10.5px] text-[var(--e-text-muted)] flex-shrink-0 ml-3">
                {new Date(a.created_at).toLocaleDateString()}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Evidence (real evidence-bundle data) ────────────────────────────────────

function EvidenceBody({ node, onNavigate }: { node: GraphNode; onNavigate?: (path: string) => void }) {
  const ev = node.evidence_bundle_ref;
  const ports = node.output_ports;
  if (!ev && !ports) {
    return <EmptyTab title="Evidence">No evidence items for this node.</EmptyTab>;
  }
  return (
    <div className="flex flex-col gap-5">
      {ev && (
        <Section label="Evidence Bundle">
          <div className="flex flex-col gap-1">
            <StatRow label="Signals" value={ev.evidence_count} />
            <StatRow label="Claims" value={ev.claim_count} />
            {ev.counter_signal_count > 0 && <StatRow label="Counter-signals" value={ev.counter_signal_count} color="#ef4444" />}
            {ev.drift_event_count > 0 && <StatRow label="Drift events" value={ev.drift_event_count} color="#eab308" />}
          </div>
        </Section>
      )}
      {ports && (ports.claims_supporting > 0 || ports.claims_contesting > 0 || ports.claims_inconclusive > 0) && (
        <Section label="Claim Graph">
          <div className="flex flex-col gap-1.5">
            <div className="flex justify-between font-mono text-[11px]">
              <span className="text-[var(--e-text-muted)]">Supporting / Contesting / Inconclusive</span>
              <span>
                <span className="text-[#22c55e]">{ports.claims_supporting}</span>
                {' / '}
                <span className="text-[#ef4444]">{ports.claims_contesting}</span>
                {' / '}
                <span className="text-[var(--e-text-muted)]">{ports.claims_inconclusive}</span>
              </span>
            </div>
            <div className="flex gap-px h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--e-env-border-subtle)' }}>
              {ports.claims_supporting > 0 && <div style={{ background: '#22c55e', flex: ports.claims_supporting }} />}
              {ports.claims_contesting > 0 && <div style={{ background: '#ef4444', flex: ports.claims_contesting }} />}
              {ports.claims_inconclusive > 0 && <div style={{ background: 'oklch(0.380 0.007 265)', flex: ports.claims_inconclusive }} />}
            </div>
            <StatRow label="Corroboration" value={`${Math.round(ports.corroboration_score * 100)}%`} />
          </div>
        </Section>
      )}
      {ev?.investigation_id && (
        <button
          onClick={() => onNavigate?.(`/workspace/investigations/${ev.investigation_id}`)}
          className="flex items-center gap-1.5 font-mono text-[11px] text-[#14b8a6] hover:underline w-fit"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Open evidence in Workspace
        </button>
      )}
    </div>
  );
}

// ── Certificate (reuses Inspector.tsx — NFR-3) ──────────────────────────────

function CertificateTab({ theatreId, title }: { theatreId: string | null; title: string }) {
  if (!theatreId) {
    return (
      <EmptyTab title="Certificate">
        No certificate — this node is not bound to a theatre. Save the composition to a theatre to see settlement
        and attestation state.
      </EmptyTab>
    );
  }
  // Reuse the existing theatre inspector — its proof/certificate section is
  // driven entirely by `useTheatreDetail(theatreId)`; the minimal nodeData
  // (only `name` is rendered) avoids re-implementing certificate rendering.
  const nodeData = { name: title, primary: false } as TheatreData;
  return (
    <div className="max-w-[420px]">
      <TheatreModuleInspector nodeData={nodeData} theatreId={theatreId} />
    </div>
  );
}

// ── Shared sub-components ────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--e-text-secondary)] mb-2.5">{label}</div>
      {children}
    </div>
  );
}

function EmptyTab({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--e-text-secondary)] mb-3">{title}</div>
      <div
        className="px-3.5 py-3 rounded-md text-[12px] text-[var(--e-text-muted)] leading-relaxed"
        style={{ border: '1px solid var(--e-env-border-subtle)', background: 'var(--e-env-bg-panel)' }}
      >
        {children}
      </div>
    </div>
  );
}

function MetricPill({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div
      className="flex flex-col gap-0.5 px-3 py-2 rounded-md"
      style={{ border: '1px solid var(--e-env-border)', background: 'var(--e-env-bg-panel)', minWidth: 120 }}
    >
      <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--e-text-muted)]">{label}</span>
      <span className="font-mono text-[14px]" style={{ color }}>
        {value}
      </span>
    </div>
  );
}

function StatRow({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="flex justify-between font-mono text-[11px]">
      <span className="text-[var(--e-text-muted)]">{label}</span>
      <span style={{ color: color ?? 'var(--e-text-secondary)' }}>{value}</span>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Non-empty string from `node.metadata` — backend truth only (FR-S3). */
function metadataString(node: GraphNode, key: string): string | null {
  const v = node.metadata?.[key];
  return typeof v === 'string' && v ? v : null;
}

function buildTags(node: GraphNode): string[] {
  const tags: string[] = [node.node_type];
  const domain = node.metadata?.domain;
  if (typeof domain === 'string' && domain) tags.push(domain);
  if (node.readiness_state) tags.push(node.readiness_state);
  if (node.verification_state) tags.push(node.verification_state);
  if (node.confidence_level) tags.push(node.confidence_level);
  return tags;
}

function buildMetrics(node: GraphNode): Array<{ label: string; value: string; color: string }> {
  const out: Array<{ label: string; value: string; color: string }> = [];
  if (node.confidence_percent != null) {
    out.push({ label: 'Confidence', value: `${node.confidence_percent}%`, color: 'oklch(0.720 0.150 278)' });
  }
  if (node.output_ports?.corroboration_score != null) {
    const pct = Math.round(node.output_ports.corroboration_score * 100);
    out.push({
      label: 'Corroboration',
      value: `${pct}%`,
      color: pct >= 70 ? '#22c55e' : pct >= 40 ? '#eab308' : 'var(--e-text-secondary)',
    });
  }
  if (node.evidence_bundle_ref?.evidence_count != null) {
    out.push({ label: 'Signals', value: String(node.evidence_bundle_ref.evidence_count), color: 'oklch(0.658 0.148 195)' });
  }
  return out;
}
